import {
  useCallback, useEffect, useRef, useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { getToken } from '@/api/client';
import { renderMarkdown } from './markdown';

// ── Resizable width ─────────────────────────────────────────────────────────
// The pane is a flex sibling of the chat; its width is user-draggable (handle on
// the LEFT boundary) and persists across opens. Mirrors the sidebar resizer
// (SidebarNav) so the whole app resizes with one muscle memory. The value is
// applied as the CSS var `--preview-w` (not a hard `width:`) so the mobile
// stacked layout — a higher-specificity rule — still wins on narrow screens.
const PREVIEW_MIN = 360;      // never so narrow the header controls crowd
const PREVIEW_DEFAULT = 480;  // matches the CSS fallback — a calm ~quarter-screen
// Storage key is versioned: an early build let the pane persist very wide widths
// (half the screen), which reopened huge and dwarfed the content. Bumping the key
// resets everyone to the sensible default once; new resizes persist under -w2.
const PREVIEW_WIDTH_KEY = 'brew-preview-w2';
const RESIZE_STEP = 24;       // keyboard nudge per arrow press
const MIN_CHAT = 440;         // the chat column must stay at least this wide so
                              // its composer never collapses into a cramped stack

/** Width the pane and chat actually SHARE. Under `.brew-shell` the children are
 *  [sidebar, main(=chat), preview] side by side, so the shared row is main+preview
 *  — NOT the parent's width (that also spans the sidebar) and NOT the viewport.
 *  main+preview is constant as the split slides, so it's a stable ceiling. Falls
 *  back to the parent/viewport pre-mount when the siblings aren't measurable yet. */
function rowWidth(aside: HTMLElement | null): number {
  const parent = aside?.parentElement;
  if (parent) {
    const main = parent.querySelector(':scope > .main');
    if (main) {
      const shared = main.getBoundingClientRect().width + aside!.getBoundingClientRect().width;
      if (shared > 0) return shared;
    }
    if (parent.clientWidth > 0) return parent.clientWidth;
  }
  return typeof window !== 'undefined' ? window.innerWidth : 1200;
}
/** Widest the pane may grow: leave at least MIN_CHAT for the chat, and never take
 *  more than 88% of the row, so the chat is always usable — measured against the
 *  actual pane+chat row, so a sidebar can't trick us into squeezing the chat. */
function maxPreviewW(aside: HTMLElement | null): number {
  const avail = rowWidth(aside);
  return Math.max(PREVIEW_MIN, Math.min(avail - MIN_CHAT, avail * 0.88));
}
function clampW(w: number, aside: HTMLElement | null): number {
  return Math.min(maxPreviewW(aside), Math.max(PREVIEW_MIN, Math.round(w)));
}
/** A comfortable "wide" preset for the one-click Expand toggle — ~58% of the row,
 *  clamped so the chat keeps its minimum. */
function widePreviewW(aside: HTMLElement | null): number {
  return clampW(Math.round(rowWidth(aside) * 0.58), aside);
}

/** The file currently open in the preview pane. */
export interface PreviewTarget {
  /** Workspace-relative path — what /api/preview resolves. */
  path: string;
  /** Display name (basename). */
  name: string;
}

/** Same-origin /api/preview URL. `download` flips the inline render into a save. */
function previewHref(path: string, download: boolean): string {
  const t = getToken();
  const q = new URLSearchParams({ path });
  if (download) q.set('download', '1');
  if (t) q.set('token', t);
  return `/api/preview?${q.toString()}`;
}

/** Types that render in the pane's iframe. Besides what the browser shows
 *  natively (html/pdf/image/text), this includes the Office formats the server
 *  converts on the fly — docx/xlsx to HTML, pptx and friends to PDF. When the
 *  server can't convert (no LibreOffice), it returns a small "download to view"
 *  card that still renders in the frame, so marking these viewable never yields a
 *  broken box. Anything NOT matched here gets the download placeholder instead. */
function isInlineViewable(name: string): boolean {
  return /\.(html?|pdf|png|jpe?g|gif|webp|svg|txt|md|json|csv|docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/i.test(name.trim());
}

/**
 * Preview pane — the Claude-Artifacts-style split view. Opens on the right, the
 * chat shrinks to the left (both are flex children of .brew-shell). Renders a
 * workspace file inline instead of forcing a new tab or a download.
 *
 * The iframe is SANDBOXED with allow-scripts but NOT allow-same-origin: a
 * generated HTML page may contain arbitrary script (charts, toggles), so it
 * needs scripting, but withholding same-origin drops it into an opaque origin
 * where it cannot touch our cookies, storage, or the token in its own URL.
 * Granting both together would defeat the sandbox — the one combination that
 * must never appear here.
 */
/** Types the server serves as a PDF (a .pdf, or an Office format it converts:
 *  pptx/ppt/doc/odt/ods/odp/rtf). Chrome's built-in PDF viewer will NOT render
 *  inside a sandboxed iframe that lacks allow-same-origin — the sandbox that
 *  contains model-authored HTML is exactly what makes a PDF show blank. A PDF
 *  from our OWN origin, rendered by the browser's trusted viewer, is safe
 *  unsandboxed (Google Drive et al. do the same), so these skip the sandbox
 *  while html/svg/converted-HTML keep it. (.docx/.xlsx render as our own HTML,
 *  not a PDF, so they stay sandboxed and render fine.) */
function rendersAsPdf(name: string): boolean {
  return /\.(pdf|pptx?|doc|odt|ods|odp|rtf)$/i.test(name.trim());
}

/** Markdown files render as RAW text in the iframe (the server serves them as
 *  text/plain), so a `.md` deliverable showed its `#`/`**`/backtick source instead
 *  of formatted output. Instead we fetch the text and render it with the SAME
 *  markdown renderer the chat uses (sanitized), so it reads like a document. */
function isMarkdown(name: string): boolean {
  return /\.(md|markdown|mdown|mkd)$/i.test(name.trim());
}

// ── Tier 0: separate preview origin (docs/architecture/live-preview.md) ──────
// When VITE_PREVIEW_ORIGIN is set (a preview subdomain), executable previews load
// from THAT origin instead of same-origin. The isolated origin lets the iframe
// use `allow-same-origin` safely, so a generated app gets REAL localStorage /
// storage / cookies (fixing the blank-preview root cause) without being able to
// touch the main app. UNSET → everything stays same-origin (current behaviour).
const PREVIEW_ORIGIN = ((import.meta.env.VITE_PREVIEW_ORIGIN as string) || '').replace(/\/+$/, '');
const CROSS_ORIGIN_PREVIEW = !!PREVIEW_ORIGIN;

/** Mint a short-lived, path-scoped preview CAPABILITY (never the account JWT — a
 *  cross-origin preview page could read its own URL). Mirrors Messages.tsx. */
async function mintPreviewCapability(filePath: string): Promise<string | null> {
  try {
    const t = getToken();
    const r = await fetch(`/api/preview-grant?path=${encodeURIComponent(filePath)}`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { token?: string };
    return j?.token ?? null;
  } catch {
    return null;
  }
}

export function PreviewPane({ target, onClose }: { target: PreviewTarget; onClose: () => void }): JSX.Element {
  const isMd = isMarkdown(target.name);
  const viewable = isInlineViewable(target.name);
  const asPdf = rendersAsPdf(target.name);

  const asideRef = useRef<HTMLElement | null>(null);

  // Markdown: fetch the source and render it (sanitized) so it reads as a
  // document, not raw `#`/`**` text. Re-fetches when the target changes; a failed
  // fetch falls back to the iframe (which at least shows the raw text).
  const [mdHtml, setMdHtml] = useState<string | null>(null);
  const [mdFailed, setMdFailed] = useState(false);
  useEffect(() => {
    if (!isMd) { setMdHtml(null); setMdFailed(false); return undefined; }
    let cancelled = false;
    setMdHtml(null); setMdFailed(false);
    fetch(previewHref(target.path, false))
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((text) => { if (!cancelled) setMdHtml(renderMarkdown(text)); })
      .catch(() => { if (!cancelled) setMdFailed(true); });
    return () => { cancelled = true; };
  }, [isMd, target.path]);

  // The iframe src. Same-origin (current) is synchronous. Cross-origin (Tier 0)
  // mints a capability and points at the preview origin; while minting, src is
  // null (a brief loading state). A failed mint falls back to same-origin.
  const [frameSrc, setFrameSrc] = useState<string | null>(
    CROSS_ORIGIN_PREVIEW ? null : previewHref(target.path, false),
  );
  useEffect(() => {
    if (isMd) return undefined;                        // markdown renders in-app, not via the iframe
    if (!CROSS_ORIGIN_PREVIEW) { setFrameSrc(previewHref(target.path, false)); return undefined; }
    let cancelled = false;
    setFrameSrc(null);
    mintPreviewCapability(target.path).then((cap) => {
      if (cancelled) return;
      if (!cap) { setFrameSrc(previewHref(target.path, false)); return; }   // fallback: same-origin
      const q = new URLSearchParams({ path: target.path, pt: cap });
      setFrameSrc(`${PREVIEW_ORIGIN}/api/preview?${q.toString()}`);
    });
    return () => { cancelled = true; };
  }, [isMd, target.path]);

  // Persisted, drag-adjustable width (see the resizer handle on the left edge).
  const [width, setWidth] = useState<number>(() => {
    try {
      const n = parseInt(localStorage.getItem(PREVIEW_WIDTH_KEY) || '', 10);
      if (Number.isFinite(n)) return clampW(n, null);
    } catch { /* private mode */ }
    return PREVIEW_DEFAULT;
  });
  useEffect(() => {
    try { localStorage.setItem(PREVIEW_WIDTH_KEY, String(width)); } catch { /* private mode */ }
  }, [width]);
  // Once mounted (and whenever the window resizes) re-clamp against the REAL
  // pane+chat row — the init above could only use the viewport as a fallback, so
  // a persisted width might otherwise leave the chat too narrow on first paint.
  useEffect(() => {
    function reclamp(): void { setWidth((w) => clampW(w, asideRef.current)); }
    reclamp();
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, []);

  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const onResizeDown = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: width };
    e.currentTarget.setPointerCapture(e.pointerId);
    // Reused legacy class: kills the width transition (so it tracks the cursor
    // without lag), forces the ew-resize cursor, and disables iframe pointer
    // events so the drag doesn't get swallowed by the previewed page.
    document.body.classList.add('is-resizing');
  }, [width]);
  const onResizeMove = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (!d) return;
    // Handle sits on the LEFT boundary — dragging left (clientX shrinks) widens.
    setWidth(clampW(d.startW + (d.startX - e.clientX), asideRef.current));
  }, []);
  const onResizeUp = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    document.body.classList.remove('is-resizing');
  }, []);
  const onResizeKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); setWidth((w) => clampW(w + RESIZE_STEP, asideRef.current)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setWidth((w) => clampW(w - RESIZE_STEP, asideRef.current)); }
  }, []);

  // One-click Expand ⇄ Restore: snap between the default and a wide preset so the
  // user doesn't have to drag to get room for a dense dashboard.
  const wide = widePreviewW(asideRef.current);
  const isWide = width >= wide - 8;
  const toggleWide = useCallback((): void => {
    setWidth((w) => {
      const target = widePreviewW(asideRef.current);
      return w >= target - 8 ? clampW(PREVIEW_DEFAULT, asideRef.current) : target;
    });
  }, []);

  return (
    <aside
      ref={asideRef}
      className="preview"
      role="complementary"
      aria-label={`Preview of ${target.name}`}
      style={{ ['--preview-w']: `${width}px` } as CSSProperties}
    >
      {/* Drag the left boundary to resize; double-click resets; arrow keys nudge. */}
      <div
        className="preview__resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize preview"
        aria-valuenow={Math.round(width)}
        aria-valuemin={PREVIEW_MIN}
        aria-valuemax={Math.round(maxPreviewW(asideRef.current))}
        tabIndex={0}
        data-tip="Drag to resize · double-click to reset"
        onPointerDown={onResizeDown}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeUp}
        onDoubleClick={(e) => { e.stopPropagation(); setWidth(PREVIEW_DEFAULT); }}
        onKeyDown={onResizeKey}
      />
      <div className="preview__header">
        <span className="preview__title" title={target.path}>{target.name}</span>
        <div className="preview__actions">
          <button type="button" className="preview__btn preview__btn--icon" onClick={toggleWide}
                  title={isWide ? 'Restore width' : 'Expand'} aria-label={isWide ? 'Restore preview width' : 'Expand preview'}>
            {isWide ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M11 7l-5 5 5 5M18 7l-5 5 5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          <a className="preview__btn preview__btn--icon" href={previewHref(target.path, false)}
             target="_blank" rel="noopener noreferrer" title="Open in a new tab">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M14 4h6v6M20 4l-8 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Open</span>
          </a>
          <a className="preview__btn" href={previewHref(target.path, true)} download>
            Download
          </a>
          <button type="button" className="preview__btn" onClick={onClose} aria-label="Close preview">
            ✕
          </button>
        </div>
      </div>
      <div className="preview__body">
        {isMd && !mdFailed ? (
          // Rendered markdown (reuses the chat's .msg__body styles so headings,
          // lists, tables, and code blocks look like a real document).
          mdHtml === null ? (
            <div className="preview__nopreview"><p>Rendering…</p></div>
          ) : (
            <div className="preview__md" style={{ padding: '28px 32px', overflow: 'auto', height: '100%' }}>
              <div className="msg__body" dangerouslySetInnerHTML={{ __html: mdHtml }} />
            </div>
          )
        ) : viewable ? (
          frameSrc === null ? (
            <div className="preview__nopreview"><p>Loading preview…</p></div>
          ) : (
          <iframe
            className="preview__frame"
            src={frameSrc}
            title={`Preview of ${target.name}`}
            // PDFs must NOT be sandboxed (the browser's PDF viewer needs it) —
            // they're passive, browser-rendered, and same-origin. For a SEPARATE
            // preview origin (Tier 0), add allow-same-origin: the isolated origin
            // makes it safe and gives the app real localStorage/storage. Same-origin
            // previews keep allow-scripts-WITHOUT-same-origin so model markup can't
            // reach our cookies/token.
            {...(asPdf ? {} : { sandbox: CROSS_ORIGIN_PREVIEW ? 'allow-scripts allow-same-origin' : 'allow-scripts' })}
            loading="lazy"
          />
          )
        ) : (
          <div className="preview__nopreview">
            <p>This file type can’t be previewed in the browser.</p>
            <a className="preview__btn" href={previewHref(target.path, true)} download>Download {target.name}</a>
          </div>
        )}
      </div>
    </aside>
  );
}
